import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiArrowLeft, FiEdit2, FiBriefcase, FiPhone,
    FiMail, FiCalendar, FiDollarSign, FiUser, FiClock,
} from "react-icons/fi";
import employeeService from "../../services/employeeService";
import salaryService from "../../services/salaryService";

const MONTHS = ["", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const STATUS_COLORS = {
    Active: { bg: "#dcfce7", color: "#166534" },
    Inactive: { bg: "#e5e7eb", color: "#475569" },
    "On Leave": { bg: "#e8f1f8", color: "#002747" },
    Resigned: { bg: "#fee2e2", color: "#b91c1c" },
};

const TABS = [
    { id: "profile", label: "Profile", icon: FiUser },
    { id: "payroll", label: "Pay History", icon: FiDollarSign },
];

// ─── Info Row ─────────────────────────────────────────────────────────────────
function InfoRow({ icon: Icon, label, value }) {
    if (!value) return null;
    return (
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start", padding: "0.5rem 0", borderBottom: "1px solid var(--border)" }}>
            <Icon size={14} color="var(--text-muted)" style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", fontWeight: 700 }}>{label}</div>
                <div style={{ fontSize: 13.5, marginTop: 1 }}>{value}</div>
            </div>
        </div>
    );
}

export default function EmployeeDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [employee, setEmployee] = useState(null);
    const [payHistory, setPayHistory] = useState([]);
    const [activeTab, setActiveTab] = useState("profile");
    const [loading, setLoading] = useState(true);
    const [loadingPay, setLoadingPay] = useState(false);

    // ── Load employee ─────────────────────────────────────────────────────────
    useEffect(() => {
        employeeService.getById(id)
            .then((res) => setEmployee(res.data))
            .catch((err) => { toast.error(err.message); navigate("/employees"); })
            .finally(() => setLoading(false));
    }, [id, navigate]);

    // ── Load pay history when tab is opened ──────────────────────────────────
    useEffect(() => {
        if (activeTab !== "payroll" || payHistory.length > 0) return;
        setLoadingPay(true);
        salaryService.getEmployeeHistory(id)
            .then((res) => setPayHistory(res.data))
            .catch((err) => toast.error(err.message))
            .finally(() => setLoadingPay(false));
    }, [activeTab, id, payHistory.length]);

    if (loading) return <div className="loading-center"><div className="spinner" />Loading employee...</div>;
    if (!employee) return null;

    const sc = STATUS_COLORS[employee.status] || STATUS_COLORS.Active;
    const initials = employee.fullName.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    {/* Avatar */}
                    <div style={{ width: 56, height: 56, borderRadius: 14, overflow: "hidden", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#475569", flexShrink: 0 }}>
                        {employee.photo?.url
                            ? <img src={employee.photo.url} alt={employee.fullName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : initials}
                    </div>
                    <div>
                        <h1 className="page-title" style={{ marginBottom: 2 }}>{employee.fullName}</h1>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                            <span className="badge badge-blue">{employee.employeeCode}</span>
                            <span style={{ background: sc.bg, color: sc.color, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>
                                {employee.status}
                            </span>
                            <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                                {employee.designation} · {employee.department}
                            </span>
                        </div>
                    </div>
                </div>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/employees")}>
                        <FiArrowLeft size={14} /> Back
                    </button>
                    <button className="btn btn-outline" onClick={() => navigate(`/employees/${id}/edit`)}>
                        <FiEdit2 size={14} /> Edit
                    </button>
                </div>
            </div>

            {/* ── Tab Bar ── */}
            <div style={{ display: "flex", gap: 4, marginBottom: "1.25rem", borderBottom: "2px solid var(--border)", paddingBottom: 0 }}>
                {TABS.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id} type="button"
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                display: "flex", alignItems: "center", gap: "0.4rem",
                                padding: "0.55rem 1rem",
                                border: "none",
                                borderBottom: isActive ? "3px solid #003b67" : "3px solid transparent",
                                background: "transparent",
                                fontSize: 13, fontWeight: isActive ? 600 : 400,
                                color: isActive ? "#003b67" : "var(--text-muted)",
                                cursor: "pointer", marginBottom: -2,
                                borderRadius: "4px 4px 0 0", transition: "all 0.15s",
                            }}
                        >
                            <tab.icon size={13} /> {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* ── Profile Tab ── */}
            {activeTab === "profile" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>

                    {/* Employment Details */}
                    <div className="card">
                        <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <FiBriefcase size={13} /> Employment Details
                        </div>
                        <div className="card-body">
                            <InfoRow icon={FiUser} label="Full Name" value={employee.fullName} />
                            <InfoRow icon={FiBriefcase} label="Category" value={employee.employeeCategory} />
                            <InfoRow icon={FiBriefcase} label="Department" value={employee.department} />
                            <InfoRow icon={FiBriefcase} label="Designation" value={employee.designation} />
                            <InfoRow icon={FiCalendar} label="Joining Date" value={employee.joiningDate} />
                            <InfoRow icon={FiCalendar} label="Date of Birth" value={employee.dateOfBirth || "—"} />
                            <InfoRow icon={FiUser} label="Gender" value={employee.gender || "—"} />
                            <InfoRow icon={FiBriefcase} label="Qualification" value={employee.qualification || "—"} />
                            <InfoRow icon={FiBriefcase} label="Experience" value={employee.experience || "—"} />
                        </div>
                    </div>

                    {/* Contact Details */}
                    <div className="card">
                        <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <FiPhone size={13} /> Contact Details
                        </div>
                        <div className="card-body">
                            <InfoRow icon={FiPhone} label="Mobile" value={employee.mobile || "—"} />
                            <InfoRow icon={FiMail} label="Email" value={employee.email || "—"} />
                            <InfoRow icon={FiUser} label="Address" value={employee.address || "—"} />
                            {employee.notes && (
                                <InfoRow icon={FiBriefcase} label="Notes" value={employee.notes} />
                            )}
                        </div>
                    </div>

                </div>
            )}

            {/* ── Pay History Tab ── */}
            {activeTab === "payroll" && (
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <FiClock size={13} /> Pay History — {employee.fullName}
                        </div>
                        <Link to="/payroll" className="btn btn-outline btn-sm">
                            <FiDollarSign size={12} /> Go to Payroll
                        </Link>
                    </div>

                    {loadingPay ? (
                        <div className="loading-center"><div className="spinner" />Loading pay history...</div>
                    ) : payHistory.length === 0 ? (
                        <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                            No salary records found for this employee.
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Month / Year</th>
                                        <th style={{ textAlign: "center" }}>Days (P/LOP)</th>
                                        <th style={{ textAlign: "right" }}>Gross Pay</th>
                                        <th style={{ textAlign: "right" }}>Deductions</th>
                                        <th style={{ textAlign: "right" }}>Net Pay</th>
                                        <th style={{ textAlign: "center" }}>Status</th>
                                        <th style={{ textAlign: "right" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payHistory.map((r) => {
                                        const isFinalized = r.status === "Finalized";
                                        return (
                                            <tr key={r._id}>
                                                <td style={{ fontWeight: 600 }}>{MONTHS[r.month]} {r.year}</td>
                                                <td style={{ textAlign: "center", fontSize: 13 }}>
                                                    {r.presentDays}P / {r.lopDays}L
                                                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>of {r.workingDays}</div>
                                                </td>
                                                <td style={{ textAlign: "right" }}>{inr(r.grossPay)}</td>
                                                <td style={{ textAlign: "right", color: "#dc2626" }}>{inr(r.totalDeductions)}</td>
                                                <td style={{ textAlign: "right", fontWeight: 700, color: "#16a34a" }}>{inr(r.netPay)}</td>
                                                <td style={{ textAlign: "center" }}>
                                                    <span style={{ background: isFinalized ? "#dcfce7" : "#fef9c3", color: isFinalized ? "#15803d" : "#92400e", padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>
                                                        {r.status}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: "right" }}>
                                                    <button className="btn btn-outline btn-sm" onClick={() => navigate(`/payroll/${r._id}`)}>
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}